import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Search, X, Minus, Loader2, Truck, Package, User as UserIcon, RefreshCw } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { createCustomerOrder } from '../../lib/customerOrders';
import { quoteCounterSale, CounterSaleQuote } from '../../lib/productSales';
import {
  ShippingMethod,
  DeliveryAreaInfo,
  StructuredAddress,
  estimateCartDeposits,
  resolveShippingBase,
  describeShippingPrice,
  computeShippingSurcharges,
  postalFsaPrefix,
} from '../../lib/storefront';
import { emptyAddress, isAddressComplete } from '../store/AddressForm';
import CustomerAddressCards from '../CustomerAddressCards';
import { fetchCustomerPricing, applyDeliveryOverrides, CustomerPricing, EMPTY_PRICING } from '../../lib/customerPricing';
import SelectCustomerModal from '../manage/SelectCustomerModal';
import EditCustomerModal from '../manage/EditCustomerModal';
import { Customer } from '../../lib/customers';
import { SidePanel, Row } from './SidePanel';
import { StoreShippingConfig, FALLBACK_SHIPPING_CONFIG, loadStoreShippingConfig } from './shippingConfig';
import { containerQtyForCheckout, depositLineName, depositPhrase } from '../../lib/deposits';

// ─────────────────────────────────────────────────────────────────────────────
// Product picker (shared by the New Order modal and the order-detail add-item)
// ─────────────────────────────────────────────────────────────────────────────

export interface PickerStockItem {
  id: string;
  name: string;
  price: number | null;
  quantity: number;
  track_inventory: boolean;
  /** Refundable per-unit container deposit in cents (0 = none). */
  deposit_cents: number;
  deposit_label?: string | null;
  tax_exempt: boolean;
  /** Rental catalog fields — rentable items can go on orders as rental lines. */
  rental_enabled?: boolean;
  rental_price_cents?: number | null;
  rental_interval?: 'month' | 'week' | null;
  photo_url?: string | null;
  category_name?: string | null;
}

/** Item can be rented (rental engine handles billing after delivery). */
export const isRentable = (i: Pick<PickerStockItem, 'rental_enabled' | 'rental_price_cents'>) =>
  i.rental_enabled === true && Number(i.rental_price_cents) > 0;

export const rentalRateLabel = (i: Pick<PickerStockItem, 'rental_price_cents' | 'rental_interval'>) =>
  `$${(Number(i.rental_price_cents) / 100).toFixed(2)}/${i.rental_interval === 'week' ? 'wk' : 'mo'}`;

export function ProductPickerOverlay({
  businessId,
  onPick,
  onClose,
  quantities,
  onAdjust,
  priceOverrides,
  rentalOnly = false,
  zIndex = 100080,
}: {
  businessId: string;
  onPick: (item: PickerStockItem) => void;
  onClose: () => void;
  /** stock_item_id → qty already on the order/cart. Items with a qty render
   *  highlighted with a stepper instead of adding a duplicate line. */
  quantities?: Record<string, number>;
  /** +1 / −1 on an item that's already on the order. */
  onAdjust?: (item: PickerStockItem, delta: 1 | -1) => void;
  /** Customer pricing: stock_item_id → this customer's price (replaces display price). */
  priceOverrides?: Record<string, number>;
  /** Show only rental-enabled items, priced by their rental rate (Start rental flow). */
  rentalOnly?: boolean;
  zIndex?: number;
}) {
  const [items, setItems] = useState<PickerStockItem[]>([]);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<string>('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      let q = supabase
        .from('stock_items')
        .select('id, name, price, quantity, track_inventory, deposit_cents, deposit_label, tax_exempt, rental_enabled, rental_price_cents, rental_interval, category:stock_categories(name)')
        .eq('business_id', businessId)
        .eq('is_deleted', false);
      q = rentalOnly
        ? q.eq('rental_enabled', true).gt('rental_price_cents', 0)
        // Sellable items + rental-only items (which may have no sale price).
        : q.or('price.not.is.null,rental_enabled.eq.true');
      const { data } = await q.order('name');
      const rows = (data || []) as any[];

      // Photos live in product_photos (same source the online store uses).
      const photos: Record<string, string> = {};
      if (rows.length > 0) {
        const { data: photoRows } = await supabase
          .from('product_photos')
          .select('stock_item_id, photo_url')
          .in('stock_item_id', rows.map(r => r.id));
        for (const p of photoRows || []) {
          if (!photos[p.stock_item_id]) photos[p.stock_item_id] = p.photo_url;
        }
      }

      setItems(
        rows.map(row => ({
          ...row,
          category_name: row.category?.name ?? null,
          photo_url: photos[row.id] ?? null,
        })) as PickerStockItem[]
      );
      setLoading(false);
    })();
  }, [businessId, rentalOnly]);

  // Category pills from the items themselves — only categories that actually
  // have sellable products, "All" first (default).
  const categories = useMemo(() => {
    const names = new Set<string>();
    for (const i of items) if (i.category_name) names.add(i.category_name);
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [items]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter(i => {
      if (q && !i.name.toLowerCase().includes(q)) return false;
      if (category !== 'all' && (i.category_name || '') !== category) return false;
      return true;
    });
  }, [items, query, category]);

  return (
    <SidePanel onClose={onClose} width="md:w-[440px]" zIndex={zIndex}>
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
          <h3 className="text-[15px] font-bold text-gray-900">{rentalOnly ? 'Add rental item' : 'Add product'}</h3>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100"><X className="w-5 h-5 text-gray-500" /></button>
        </div>
        <div className="px-5 py-3 flex-shrink-0 space-y-2.5">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search products"
              className="w-full pl-9 pr-3 py-2 rounded-[10px] border border-gray-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
            />
          </div>
          {categories.length > 0 && (
            <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
              {['all', ...categories].map(c => (
                <button
                  key={c}
                  onClick={() => setCategory(c)}
                  className={`px-3 py-1.5 rounded-full text-[12px] font-semibold whitespace-nowrap transition-colors ${
                    category === c ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {c === 'all' ? 'All' : c}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex-1 overflow-y-auto px-5 pb-5 space-y-1.5">
          {loading ? (
            <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 text-gray-400 animate-spin" /></div>
          ) : filtered.length === 0 ? (
            <p className="text-[13px] text-gray-500 text-center py-8">No products found</p>
          ) : (
            filtered.map(item => {
              const onOrder = quantities?.[item.id] ?? 0;
              const hasStepper = onOrder > 0 && !!onAdjust;
              const displayPrice = priceOverrides?.[item.id] ?? Number(item.price ?? 0);
              const isCustomerPrice = priceOverrides?.[item.id] != null && priceOverrides[item.id] !== Number(item.price ?? 0);
              const thumb = (
                <div className="w-12 h-12 bg-gray-50 rounded-lg border border-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {item.photo_url ? (
                    <img src={item.photo_url} alt={item.name} loading="lazy" className="w-full h-full object-cover" />
                  ) : (
                    <Package className="h-5 w-5 text-gray-300" />
                  )}
                </div>
              );
              const info = (
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-medium text-gray-900 truncate">{item.name}</p>
                  <p className="text-[12px] text-gray-500 truncate">
                    {item.track_inventory === false ? 'Made to order' : `${item.quantity} in stock`}
                    {!rentalOnly && isRentable(item) && (
                      <span className="text-indigo-600"> · Rents {rentalRateLabel(item)}</span>
                    )}
                    {Number(item.deposit_cents) > 0 && (
                      <span className="text-amber-600"> · +${(Number(item.deposit_cents) / 100).toFixed(2)} deposit</span>
                    )}
                    {hasStepper && <span className="text-blue-600"> · ${displayPrice.toFixed(2)} each</span>}
                    {isCustomerPrice && <span className="text-violet-600"> · customer price</span>}
                  </p>
                </div>
              );
              // Already on the order — highlight it and adjust the existing
              // line with a stepper instead of adding a duplicate.
              if (hasStepper) {
                return (
                  <div
                    key={item.id}
                    className="w-full flex items-center gap-3 rounded-xl border border-blue-400 bg-blue-50/50 px-3 py-2.5"
                  >
                    {thumb}
                    {info}
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <button
                        onClick={() => onAdjust!(item, -1)}
                        className="w-7 h-7 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:bg-gray-50"
                      >
                        <Minus className="w-3.5 h-3.5 text-gray-600" />
                      </button>
                      <span className="w-7 text-center text-[13px] font-bold text-blue-700">{onOrder}</span>
                      <button
                        onClick={() => onAdjust!(item, 1)}
                        className="w-7 h-7 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:bg-gray-50"
                      >
                        <Plus className="w-3.5 h-3.5 text-gray-600" />
                      </button>
                    </div>
                  </div>
                );
              }
              return (
                <button
                  key={item.id}
                  onClick={() => onPick(item)}
                  className="w-full text-left flex items-center gap-3 rounded-xl border border-gray-200 px-3 py-2.5 hover:border-blue-300 hover:bg-blue-50/30 transition-colors"
                >
                  {thumb}
                  {info}
                  <span className={`text-[13px] font-semibold flex-shrink-0 ${
                    rentalOnly || (item.price == null && isRentable(item)) ? 'text-indigo-700' : isCustomerPrice ? 'text-violet-700' : 'text-gray-900'
                  }`}>
                    {rentalOnly || (item.price == null && isRentable(item)) ? rentalRateLabel(item) : `$${displayPrice.toFixed(2)}`}
                  </span>
                </button>
              );
            })
          )}
        </div>
    </SidePanel>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// New order (staff-entered, e.g. phone orders) — built like an online order:
// same shipping methods (area pricing + product surcharges), same automatic
// bottle-deposit lines with container-swap logic, same structured address.
// ─────────────────────────────────────────────────────────────────────────────

/** Booking-modal style section card. */
export function Section({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] p-4">
      <div className="flex items-center justify-between mb-2.5">
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{title}</h4>
        {action}
      </div>
      {children}
    </div>
  );
}

const inputClass =
  'w-full rounded-xl border border-gray-200 px-3 py-2.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400';

export function NewOrderModal({
  businessId,
  onClose,
  onCreated,
}: {
  businessId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  // Each line keeps the immutable catalog price (catalogPrice) plus the
  // customer-effective baseline (basePrice — catalog or the customer's
  // custom/tier price) so a one-off edit can be spotted and reset; `price` is
  // what actually bills. priceText holds the in-progress input string so
  // typing "12." doesn't snap to a number.
  // isRental: the line goes on as a rental (recurring billing starts at
  // delivery) instead of a one-time sale. Rentable items can toggle.
  const [items, setItems] = useState<Array<PickerStockItem & { orderQty: number; catalogPrice: number; catalogDepositCents: number; basePrice: number; priceText: string; isRental: boolean }>>([]);
  const [showPicker, setShowPicker] = useState(false);

  // Customer (required — staff orders are always tied to a customer record)
  const [showCustomerPicker, setShowCustomerPicker] = useState(false);
  const [showEditCustomer, setShowEditCustomer] = useState(false);
  const [customer, setCustomer] = useState<Customer | null>(null);
  /** Per-address container counts. Null when the by-address view isn't available yet. */
  const [containerSites, setContainerSites] = useState<Array<{ stock_item_id: string; quantity: number; customer_address_id: string | null }> | null>(null);
  const [accountBalances, setAccountBalances] = useState<Record<string, number>>({});
  /** This customer's custom pricing: item prices, deposits, delivery charges. */
  const [pricing, setPricing] = useState<CustomerPricing>(EMPTY_PRICING);

  // Fulfillment: 'pickup' | 'shipping-N' (configured methods) | 'basic-delivery' (no store config)
  const [config, setConfig] = useState<StoreShippingConfig | null>(null);
  const [method, setMethod] = useState<string>('');
  // addr mirrors the SELECTED address-book card (source of truth is
  // customer_addresses — the picker writes there, we just consume it here
  // for pricing + the order snapshot).
  const [addr, setAddr] = useState<StructuredAddress>(emptyAddress());
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null);
  /** Legacy customers.address text — seeds the add-address form when the book is empty. */
  const [legacyAddressText, setLegacyAddressText] = useState<string>('');

  const [note, setNote] = useState('');
  const [poNumber, setPoNumber] = useState('');
  // Mute switch: created orders send no store notifications until unmuted.
  const [muteNotifications, setMuteNotifications] = useState(false);
  const [busy, setBusy] = useState(false);
  const [quote, setQuote] = useState<CounterSaleQuote | null>(null);

  // ── Store shipping config (same source the online checkout uses) ──────────
  useEffect(() => {
    (async () => {
      try {
        const cfg = await loadStoreShippingConfig(businessId);
        setConfig(cfg);
        setMethod(cfg.shippingMethods.length > 0 ? 'shipping-0' : 'basic-delivery');
      } catch {
        setConfig(FALLBACK_SHIPPING_CONFIG);
        setMethod('basic-delivery');
      }
    })();
  }, [businessId]);

  // ── Customer selection: prefill address + load container balances ─────────
  const handleSelectCustomer = useCallback(async (c: Customer) => {
    setCustomer(c);
    setShowCustomerPicker(false);
    try {
      const [{ data: row }, { data: balanceRows }, pricingData] = await Promise.all([
        supabase
          .from('customers')
          .select('default_shipping_address, address, company')
          .eq('id', c.id)
          .maybeSingle(),
        supabase
          .from('customer_container_balances_by_address')
          .select('stock_item_id, customer_address_id, quantity')
          .eq('business_id', businessId)
          .eq('customer_id', c.id),
        fetchCustomerPricing(businessId, c.id).catch(() => EMPTY_PRICING),
      ]);
      // Delivery address comes from the address-book picker (it auto-selects
      // the customer's default card) — here we only keep the legacy free-text
      // address around to seed the add form for customers with no cards yet.
      setLegacyAddressText(row?.address || '');
      setAddr(emptyAddress());
      setSelectedAddressId(null);
      if (balanceRows) {
        setContainerSites(balanceRows.map(b => ({
          stock_item_id: b.stock_item_id,
          customer_address_id: b.customer_address_id,
          quantity: Number(b.quantity) || 0,
        })));
        setAccountBalances({});
      } else {
        setContainerSites(null);
        const { data: legacy } = await supabase
          .from('customer_container_balances')
          .select('stock_item_id, quantity')
          .eq('business_id', businessId)
          .eq('customer_id', c.id);
        const map: Record<string, number> = {};
        for (const b of legacy || []) {
          if (Number(b.quantity) > 0) map[b.stock_item_id] = Number(b.quantity);
        }
        setAccountBalances(map);
      }
      setPricing(pricingData);
      // Reprice lines already in the draft: the customer's price/deposit
      // becomes the baseline (staff can still one-off edit from there).
      setItems(prev =>
        prev.map(i => {
          const deposit = pricingData.deposits[i.id] ?? i.catalogDepositCents;
          // Rental lines keep their rate — customer item prices are sale prices.
          if (i.isRental) return { ...i, deposit_cents: deposit };
          const base = pricingData.prices[i.id] ?? i.catalogPrice;
          return {
            ...i,
            basePrice: base,
            price: base,
            priceText: base.toFixed(2),
            deposit_cents: deposit,
          };
        })
      );
    } catch {
      setBalances({});
      setPricing(EMPTY_PRICING);
    }
  }, [businessId]);

  const clearCustomer = () => {
    setCustomer(null);
    setBalances({});
    setPricing(EMPTY_PRICING);
    setAddr(emptyAddress());
    setSelectedAddressId(null);
    setLegacyAddressText('');
    // Back to catalog pricing until another customer is chosen.
    setItems(prev =>
      prev.map(i => (i.isRental
        ? { ...i, deposit_cents: i.catalogDepositCents }
        : {
            ...i,
            basePrice: i.catalogPrice,
            price: i.catalogPrice,
            priceText: i.catalogPrice.toFixed(2),
            deposit_cents: i.catalogDepositCents,
          }))
    );
  };

  // ── Cart helpers (same math as the online checkout) ───────────────────────
  const cart = useMemo(
    () => items.map(i => ({
      product: {
        id: i.id,
        name: i.name,
        deposit_cents: Number(i.deposit_cents) || 0,
        deposit_label: i.deposit_label,
      },
      quantity: i.orderQty,
    })),
    [items]
  );

  const isPickup = method === 'pickup';
  const balances = useMemo(() => {
    if (!containerSites) return accountBalances;
    return containerQtyForCheckout(containerSites, {
      pickup: isPickup,
      addressId: selectedAddressId,
    });
  }, [containerSites, accountBalances, isPickup, selectedAddressId]);

  const deposits = useMemo(
    () => estimateCartDeposits(cart, customer ? balances : null),
    [cart, customer, balances]
  );
  // Customer delivery overrides (matched by method name) replace base prices.
  const effectiveShippingMethods = useMemo(
    () => applyDeliveryOverrides(config?.shippingMethods || [], pricing.delivery),
    [config, pricing.delivery]
  );
  const selectedShipping: ShippingMethod | null = useMemo(() => {
    if (!config || !method.startsWith('shipping-')) return null;
    return effectiveShippingMethods[parseInt(method.replace('shipping-', ''), 10)] || null;
  }, [config, effectiveShippingMethods, method]);

  const shippingBase = useMemo(
    () => (selectedShipping ? resolveShippingBase(selectedShipping, addr, config?.areas) : null),
    [selectedShipping, addr, config]
  );
  const surchargeLines = useMemo(
    () => (selectedShipping ? computeShippingSurcharges(selectedShipping, cart) : []),
    [selectedShipping, cart]
  );

  /** The full order line set — product lines + delivery fee lines + deposit lines. */
  const buildOrderItems = useCallback(() => {
    const orderItems: Array<{
      stock_item_id: string | null;
      product_name: string;
      quantity: number;
      unit_price: number;
      line_type?: 'product' | 'fee' | 'deposit' | 'rental';
      deposit_stock_item_id?: string | null;
      taxable?: boolean;
    }> = items.map(i => (i.isRental
      ? {
          // Rental line: unit_price is the recurring rate; the pending
          // agreement is created server-side and activates at delivery.
          stock_item_id: i.id,
          product_name: `Rental — ${i.name} (first ${i.rental_interval === 'week' ? 'week' : 'month'})`,
          quantity: i.orderQty,
          unit_price: Number(i.price ?? 0),
          line_type: 'rental' as const,
          taxable: i.tax_exempt !== true,
        }
      : {
          stock_item_id: i.id,
          product_name: i.name,
          quantity: i.orderQty,
          unit_price: Number(i.price ?? 0),
        }));

    if (selectedShipping && shippingBase?.status === 'priced' && shippingBase.price > 0) {
      orderItems.push({
        stock_item_id: null,
        product_name: `Delivery — ${selectedShipping.name}`,
        quantity: 1,
        unit_price: Math.round(shippingBase.price * 100) / 100,
        line_type: 'fee',
      });
    }
    for (const s of surchargeLines) {
      orderItems.push({
        stock_item_id: null,
        product_name: s.label,
        quantity: 1,
        unit_price: Math.round(s.amount * 100) / 100,
        line_type: 'fee',
      });
    }
    for (const line of deposits.lines) {
      const netNew = line.quantity - line.swapped;
      if (netNew <= 0) continue;
      const item = items.find(i => i.id === line.productId);
      orderItems.push({
        stock_item_id: null,
        product_name: depositLineName(line.label, line.name),
        quantity: netNew,
        unit_price: (Number(item?.deposit_cents) || 0) / 100,
        line_type: 'deposit',
        deposit_stock_item_id: line.productId,
        taxable: false,
      });
    }
    return orderItems;
  }, [items, selectedShipping, shippingBase, surchargeLines, deposits]);

  // ── Live estimate (server-quoted so tax matches the eventual invoice) ─────
  useEffect(() => {
    if (items.length === 0) {
      setQuote(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        setQuote(await quoteCounterSale(businessId, buildOrderItems(), null));
      } catch { /* estimate only */ }
    }, 250);
    return () => clearTimeout(t);
  }, [items, businessId, buildOrderItems]);

  const handlePick = (item: PickerStockItem) => {
    setItems(prev => {
      const existing = prev.find(i => i.id === item.id);
      if (existing) return prev.map(i => (i.id === item.id ? { ...i, orderQty: i.orderQty + 1 } : i));
      // Rental-only items (no sale price) start as rentals; dual-purpose
      // items start as a sale with a Rent toggle on the line.
      const startAsRental = isRentable(item) && item.price == null;
      const catalog = Number(item.price ?? 0);
      const rentalRate = Number(item.rental_price_cents ?? 0) / 100;
      const base = startAsRental ? rentalRate : (pricing.prices[item.id] ?? catalog);
      const catalogDeposit = Number(item.deposit_cents) || 0;
      return [...prev, {
        ...item,
        price: base,
        deposit_cents: pricing.deposits[item.id] ?? catalogDeposit,
        orderQty: 1,
        catalogPrice: catalog,
        catalogDepositCents: catalogDeposit,
        basePrice: base,
        priceText: base.toFixed(2),
        isRental: startAsRental,
      }];
    });
    setShowPicker(false);
  };

  /** Flip a rentable line between one-time sale and recurring rental. */
  const setLineRental = (id: string, rent: boolean) => {
    setItems(prev =>
      prev.map(i => {
        if (i.id !== id || i.isRental === rent) return i;
        const base = rent
          ? Number(i.rental_price_cents ?? 0) / 100
          : (pricing.prices[i.id] ?? i.catalogPrice);
        return { ...i, isRental: rent, basePrice: base, price: base, priceText: base.toFixed(2) };
      })
    );
  };

  const setQty = (id: string, qty: number) => {
    setItems(prev => (qty <= 0 ? prev.filter(i => i.id !== id) : prev.map(i => (i.id === id ? { ...i, orderQty: qty } : i))));
  };

  /** One-time custom price for this order only — the catalog item is untouched. */
  const setPrice = (id: string, text: string) => {
    const parsed = parseFloat(text);
    setItems(prev =>
      prev.map(i =>
        i.id === id ? { ...i, priceText: text, price: Number.isFinite(parsed) && parsed >= 0 ? parsed : 0 } : i
      )
    );
  };

  const resetPrice = (id: string) => {
    setItems(prev => prev.map(i => (i.id === id ? { ...i, price: i.basePrice, priceText: i.basePrice.toFixed(2) } : i)));
  };

  // ── Validation ─────────────────────────────────────────────────────────────
  const addressPrefix = postalFsaPrefix(addr.postal_code);
  const outsideEnforcedArea =
    !isPickup && config?.enforced && config.enforcedPrefixes.length > 0 &&
    addressPrefix.length === 3 && !config.enforcedPrefixes.includes(addressPrefix);

  const deliveryReady = useMemo(() => {
    if (isPickup) return true;
    if (selectedShipping) {
      // Configured method: needs a complete address, an in-area price for
      // by_area methods, and (when enforcement is on) an in-area postal code.
      if (!isAddressComplete(addr)) return false;
      if (shippingBase?.status !== 'priced') return false;
      if (outsideEnforcedArea) return false;
      return true;
    }
    // Basic delivery (no online store config): just need somewhere to go.
    return addr.line1.trim().length > 0;
  }, [isPickup, selectedShipping, addr, shippingBase, outsideEnforcedArea]);

  const canCreate = items.length > 0 && !!customer && deliveryReady && !busy;

  const handleCreate = async () => {
    if (!canCreate || !customer) return;
    setBusy(true);
    try {
      const expectedReturns = deposits.lines
        .filter(l => l.swapped > 0)
        .map(l => ({ stock_item_id: l.productId, returned: l.swapped }));
      const contactName = `${customer.first_name ?? ''} ${customer.last_name ?? ''}`.trim();
      await createCustomerOrder({
        businessId,
        items: buildOrderItems(),
        customerId: customer.id,
        contact: {
          name: contactName || null,
          email: customer.email || null,
          phone: customer.phone || null,
        },
        fulfillment: {
          type: isPickup ? 'pickup' : 'delivery',
          address: isPickup ? undefined : (addr.formatted || addr.line1 || undefined),
          expected_returns: expectedReturns.length > 0 ? expectedReturns : null,
          suppress_notifications: muteNotifications,
        },
        source: 'staff',
        customerNote: note || null,
        poNumber: poNumber.trim() || null,
        shippingAddress: !isPickup && isAddressComplete(addr) ? addr : null,
        deliveryAddressId: !isPickup && selectedAddressId ? selectedAddressId : null,
      });
      onCreated();
    } catch (err: any) {
      alert(err?.message || 'Failed to create order');
    } finally {
      setBusy(false);
    }
  };

  // ── Delivery option rendering (same labels as the online checkout) ────────
  const deliveryOptions = useMemo(() => {
    if (!config) return [];
    const opts: Array<{ id: string; title: string; subtitle?: string; priceLabel: string; disabled: boolean }> = [];
    if (effectiveShippingMethods.length > 0) {
      effectiveShippingMethods.forEach((m, idx) => {
        const desc = describeShippingPrice(m, addr, config.areas);
        opts.push({
          id: `shipping-${idx}`,
          title: m.name || 'Delivery',
          subtitle: desc.state === 'needs_address' ? 'Price depends on the delivery address' : undefined,
          priceLabel: desc.state === 'needs_address' ? 'Enter address' : desc.label,
          disabled: desc.state === 'unavailable',
        });
      });
    } else {
      opts.push({ id: 'basic-delivery', title: 'Delivery', priceLabel: 'FREE', disabled: false });
    }
    if (config.enableInStorePickup) {
      opts.push({
        id: 'pickup',
        title: 'Pickup',
        subtitle: config.pickupAddress || undefined,
        priceLabel: 'FREE',
        disabled: false,
      });
    }
    return opts;
  }, [config, effectiveShippingMethods, addr]);

  const deliveryCents = Math.round(
    ((shippingBase?.status === 'priced' ? shippingBase.price : 0) + surchargeLines.reduce((n, s) => n + s.amount, 0)) * 100
  );
  const productSubtotalCents = items.reduce((n, i) => n + Math.round(Number(i.price ?? 0) * 100) * i.orderQty, 0);

  const customerInitials = customer
    ? `${(customer.first_name || ' ')[0]}${(customer.last_name || ' ')[0]}`.trim().toUpperCase() || 'C'
    : '';

  return (
    <SidePanel onClose={onClose} width="md:w-[600px]" zIndex={100060}>
        {/* Header — booking modal style */}
        <div className="relative bg-gray-50 border-b border-gray-200 px-6 py-5 text-center flex-shrink-0">
          <h3 className="text-xl font-bold text-gray-900" style={{ letterSpacing: '-0.02em' }}>New Order</h3>
          <button
            onClick={onClose}
            className="absolute right-4 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-gray-200/70 hover:bg-gray-300 flex items-center justify-center"
          >
            <X className="w-[18px] h-[18px] text-gray-600" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 md:p-5 space-y-4 bg-gray-50/50">
          <Section title="PO number" action={<span className="text-[11px] font-medium text-gray-400">Optional</span>}>
            <input
              value={poNumber}
              onChange={(e) => setPoNumber(e.target.value)}
              placeholder="Customer PO number"
              autoComplete="off"
              className={inputClass}
            />
          </Section>

          {/* Customer */}
          <Section
            title="Customer"
            action={customer ? (
              <button onClick={clearCustomer} className="text-[12px] font-semibold text-blue-600 hover:text-blue-700">
                Change
              </button>
            ) : undefined}
          >
            {customer ? (
              <button
                type="button"
                onClick={() => setShowEditCustomer(true)}
                className="w-full text-left bg-gray-50 rounded-xl p-3 flex items-center gap-3 hover:bg-gray-100 active:scale-[0.99] transition touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[13px] font-bold flex-shrink-0">
                  {customerInitials}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[14px] font-semibold text-gray-900 truncate">
                    {`${customer.first_name ?? ''} ${customer.last_name ?? ''}`.trim() || customer.email}
                  </p>
                  {customer.company && (
                    <p className="text-[12px] font-medium text-gray-600 truncate">{customer.company}</p>
                  )}
                  <p className="text-[12px] text-gray-500 truncate">
                    {[customer.phone, customer.email].filter(Boolean).join(' · ') || 'Tap to add contact details'}
                  </p>
                </div>
              </button>
            ) : (
              <button
                onClick={() => setShowCustomerPicker(true)}
                className="w-full py-3 rounded-xl border-2 border-dashed border-blue-300 text-[13px] font-semibold text-blue-600 hover:bg-blue-50/50 transition-colors inline-flex items-center justify-center gap-1.5"
              >
                <UserIcon className="w-4 h-4" /> Add customer
              </button>
            )}
          </Section>

          {/* Items */}
          <Section
            title="Items"
            action={items.length > 0 ? (
              <button onClick={() => setShowPicker(true)} className="text-[12px] font-semibold text-blue-600 hover:text-blue-700 inline-flex items-center gap-1">
                <Plus className="w-3.5 h-3.5" /> Add product
              </button>
            ) : undefined}
          >
            {items.length === 0 ? (
              <button
                onClick={() => setShowPicker(true)}
                className="w-full py-6 rounded-xl border-2 border-dashed border-blue-300 text-[13px] font-semibold text-blue-600 hover:bg-blue-50/50 transition-colors"
              >
                <Plus className="w-4 h-4 inline mr-1 -mt-0.5" /> Add products
              </button>
            ) : (
              <div className="space-y-2">
                {items.map(item => {
                  const isCustomPrice = Number(item.price ?? 0) !== item.basePrice;
                  return (
                  <div key={item.id} className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 ${
                    item.isRental ? 'border-indigo-200 bg-indigo-50/40' : 'border-gray-100 bg-gray-50/50'
                  }`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-medium text-gray-900 truncate">
                        {item.name}
                        {item.isRental && (
                          <span className="ml-1.5 inline-flex items-center rounded-full bg-indigo-100 text-indigo-700 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide align-middle">
                            Rental
                          </span>
                        )}
                      </p>
                      <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                        {/* Editable unit price — one-time custom price for this order */}
                        <span className={`inline-flex items-center rounded-lg border bg-white px-1.5 ${isCustomPrice ? 'border-violet-300' : 'border-gray-200'}`}>
                          <span className="text-[12px] text-gray-400">$</span>
                          <input
                            inputMode="decimal"
                            value={item.priceText}
                            onChange={(e) => setPrice(item.id, e.target.value)}
                            onBlur={() => setPrice(item.id, (Number(item.price ?? 0)).toFixed(2))}
                            className="w-14 py-0.5 text-[12px] font-semibold text-gray-900 bg-transparent focus:outline-none"
                            aria-label={`Unit price for ${item.name}`}
                          />
                        </span>
                        <span className="text-[12px] text-gray-500">
                          {item.isRental ? `/${item.rental_interval === 'week' ? 'week' : 'month'}` : 'each'}
                        </span>
                        {isRentable(item) && (
                          <span className="inline-flex rounded-lg border border-gray-200 bg-white overflow-hidden">
                            <button
                              onClick={() => setLineRental(item.id, false)}
                              className={`px-2 py-0.5 text-[11px] font-semibold ${!item.isRental ? 'bg-gray-800 text-white' : 'text-gray-500 hover:bg-gray-50'}`}
                            >
                              Buy
                            </button>
                            <button
                              onClick={() => setLineRental(item.id, true)}
                              className={`px-2 py-0.5 text-[11px] font-semibold ${item.isRental ? 'bg-indigo-600 text-white' : 'text-gray-500 hover:bg-gray-50'}`}
                            >
                              Rent
                            </button>
                          </span>
                        )}
                        {isCustomPrice && (
                          <>
                            <span className="text-[11px] text-gray-400 line-through">${item.basePrice.toFixed(2)}</span>
                            <button
                              onClick={() => resetPrice(item.id)}
                              className="text-[11px] font-semibold text-violet-600 hover:text-violet-800"
                            >
                              Reset
                            </button>
                          </>
                        )}
                        {!isCustomPrice && item.basePrice !== item.catalogPrice && (
                          <span className="text-[11px] font-semibold text-violet-600">customer price</span>
                        )}
                        {Number(item.deposit_cents) > 0 && (
                          <span className="text-[12px] text-amber-600">· +${(Number(item.deposit_cents) / 100).toFixed(2)} deposit</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => setQty(item.id, item.orderQty - 1)} className="w-7 h-7 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:bg-gray-50">
                        <Minus className="w-3.5 h-3.5 text-gray-600" />
                      </button>
                      <span className="w-8 text-center text-[13px] font-semibold text-gray-900">{item.orderQty}</span>
                      <button onClick={() => setQty(item.id, item.orderQty + 1)} className="w-7 h-7 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:bg-gray-50">
                        <Plus className="w-3.5 h-3.5 text-gray-600" />
                      </button>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
            {customer && deposits.swappedTotal > 0 && (
              <p className="mt-2 text-[12px] text-emerald-700 bg-emerald-50 rounded-lg px-2.5 py-1.5 inline-flex items-center gap-1.5">
                <RefreshCw className="w-3.5 h-3.5" />
                Customer has containers on hand — assuming {deposits.swappedTotal} swapped at delivery
              </p>
            )}
          </Section>

          {/* Delivery method — same options and pricing as the online store */}
          <Section title="Delivery method">
            {!config ? (
              <div className="py-4 flex justify-center"><Loader2 className="w-5 h-5 text-gray-400 animate-spin" /></div>
            ) : (
              <div className="space-y-2">
                {deliveryOptions.map(opt => {
                  const selected = method === opt.id;
                  const isPickupOpt = opt.id === 'pickup';
                  return (
                    <button
                      key={opt.id}
                      disabled={opt.disabled}
                      onClick={() => setMethod(opt.id)}
                      className={`w-full text-left rounded-xl border-2 px-3.5 py-3 flex items-center gap-3 transition-colors ${
                        selected ? 'border-blue-500 bg-blue-50/40' : 'border-gray-200 hover:border-gray-300'
                      } ${opt.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <span className={`w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                        selected ? 'border-blue-600' : 'border-gray-300'
                      }`}>
                        {selected && <span className="w-2.5 h-2.5 rounded-full bg-blue-600" />}
                      </span>
                      {isPickupOpt ? <Package className="w-4 h-4 text-emerald-600 flex-shrink-0" /> : <Truck className="w-4 h-4 text-blue-600 flex-shrink-0" />}
                      <span className="flex-1 min-w-0">
                        <span className="block text-[13px] font-semibold text-gray-900">{opt.title}</span>
                        {opt.subtitle && <span className="block text-[12px] text-gray-500 truncate">{opt.subtitle}</span>}
                      </span>
                      <span className={`text-[13px] font-semibold flex-shrink-0 ${
                        opt.disabled ? 'text-gray-400' : opt.priceLabel === 'FREE' ? 'text-emerald-600' : 'text-gray-900'
                      }`}>
                        {opt.disabled ? 'Unavailable' : opt.priceLabel}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </Section>

          {/* Delivery address — the customer's address book (source of truth).
              The default card is preselected; adding/editing here saves to the
              customer profile so it's there on every future order. */}
          {!isPickup && config && (
            <Section title="Delivery address">
              {customer ? (
                <CustomerAddressCards
                  businessId={businessId}
                  customerId={customer.id}
                  mode="pick"
                  selectedId={selectedAddressId}
                  onSelect={(card, structured) => {
                    setSelectedAddressId(card?.id ?? null);
                    setAddr(structured ?? emptyAddress());
                  }}
                  areaPrefixes={config.enforced && config.enforcedPrefixes.length > 0 ? config.enforcedPrefixes : null}
                  prefillLine1={legacyAddressText}
                />
              ) : (
                <p className="text-[13px] text-gray-500 py-1">
                  Select a customer to choose their delivery address.
                </p>
              )}
              {outsideEnforcedArea && (
                <p className="mt-2 text-[12px] font-medium text-red-600">
                  This address is outside the configured delivery areas.
                </p>
              )}
            </Section>
          )}

          {/* Order note */}
          <Section title="Order note">
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="Notes from the customer (gate code, where to leave it…)"
              className={`${inputClass} resize-none`}
            />
          </Section>

          {/* Notifications */}
          <Section title="Notifications">
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={muteNotifications}
                onChange={(e) => setMuteNotifications(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="min-w-0">
                <span className="block text-[13px] font-semibold text-gray-900">No notifications</span>
                <span className="block text-[12px] text-gray-500">
                  Nothing is sent to the customer for this order — no confirmation, status, or invoice
                  emails. You can unmute anytime from the order.
                </span>
              </span>
            </label>
          </Section>

          {/* Summary */}
          {items.length > 0 && (
            <Section title="Summary">
              <div className="space-y-1">
                <Row label="Subtotal" value={`$${(productSubtotalCents / 100).toFixed(2)}`} />
                {deliveryCents > 0 && <Row label="Delivery" value={`$${(deliveryCents / 100).toFixed(2)}`} />}
                {surchargeLines.map(s => (
                  <p key={s.stockItemId} className="text-[11px] text-gray-400 pl-3">
                    incl. {s.label} — ${s.amount.toFixed(2)}
                  </p>
                ))}
                {deposits.lines.filter(l => l.charge > 0).map(l => (
                  <Row
                    key={l.productId}
                    label={`${depositPhrase(l.label)} — ${l.name}`}
                    value={`$${l.charge.toFixed(2)}`}
                    accent="text-amber-700"
                  />
                ))}
                {quote && <Row label="Tax" value={`$${quote.tax.toFixed(2)}`} />}
                <div className="border-t border-gray-200 pt-1.5 mt-1.5">
                  <Row label="Estimated total" value={quote ? `$${quote.total.toFixed(2)}` : '—'} bold />
                </div>
                <p className="text-[11px] text-gray-400 pt-0.5">Final total is calculated when the invoice is generated.</p>
                {items.some(i => i.isRental) && (
                  <p className="text-[11px] font-medium text-indigo-600 pt-0.5">
                    Rental lines start recurring billing when the order is delivered — the first period and deposit go on the delivery invoice.
                  </p>
                )}
              </div>
            </Section>
          )}
        </div>

        {/* Footer CTA — booking modal style */}
        <div className="flex-shrink-0 border-t border-gray-200 bg-white px-4 py-3" style={{ boxShadow: '0 -2px 10px rgba(0,0,0,0.04)' }}>
          <button
            disabled={!canCreate}
            onClick={handleCreate}
            className={`w-full py-3 rounded-xl text-[15px] font-semibold text-white inline-flex items-center justify-center gap-2 transition-colors ${
              canCreate ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-300 cursor-not-allowed'
            }`}
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            Create order{quote && items.length > 0 ? ` · $${quote.total.toFixed(2)}` : ''}
          </button>
        </div>

      {showPicker && (
        <ProductPickerOverlay
          businessId={businessId}
          onPick={handlePick}
          onClose={() => setShowPicker(false)}
          priceOverrides={pricing.prices}
          quantities={Object.fromEntries(items.map(i => [i.id, i.orderQty]))}
          onAdjust={(item, delta) => {
            const existing = items.find(i => i.id === item.id);
            if (existing) setQty(item.id, existing.orderQty + delta);
          }}
        />
      )}
      <SelectCustomerModal
        isOpen={showCustomerPicker}
        onClose={() => setShowCustomerPicker(false)}
        onSelect={handleSelectCustomer}
      />
      <EditCustomerModal
        isOpen={showEditCustomer}
        onClose={() => setShowEditCustomer(false)}
        onSuccess={(updated) => {
          setShowEditCustomer(false);
          setCustomer(updated);
        }}
        customer={customer}
      />
    </SidePanel>
  );
}
